/* eslint-disable no-unused-vars */
import axios from 'axios';
import { getToken } from '../../utils/token.js';
import { fetchingUserFailure, fetchingUserSuccess } from '../../redux/user/userSlice.js';
import { fetchingGroupSuccess } from '../../redux/group/groupSlice.js';
import { fetchingDeviceSuccess } from '../../redux/device/deviceSlice.js';
import { fetchingApplicationSuccess } from '../../redux/application/applicationSlice.js';
import { fetchingAdvancedSearchFailure, fetchingAdvancedSearchSuccess, fetchingAdvancedSearchStart, fetchingPageStart } from '../../redux/advancedSearch/advancedSearchSlice.js';
import { fetchingDelegationSuccess } from '../../redux/delegation/delegationSlice.js';
import { fetchingOrganizationSuccess } from '../../redux/organization/organizationSlice.js';

let baseUrl = null;

if (process.env.NODE_ENV == 'production') {
    baseUrl = process.env.REACT_APP_API_PRODUCTION
} else {
    baseUrl = process.env.REACT_APP_API_DEV
}

export const getUserInfo = async (dispatch, setButtonsMenue, upn, signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/informations/${upn}`, {
            headers: { 'Authorization' : token },
            signal: signal
        })
        setButtonsMenue(buttonsMenue => ({ 
            ...buttonsMenue, activeButtonIndex: buttonsMenue.buttons[0].id
        }));
        if (res.status === 200) {
            dispatch(fetchingUserSuccess(res.data));
            return res;
        }
    } catch (error) {
        // error.response est absent si l'erreur vient de l'acquisition du token
        // ou du réseau — sans l'optional chaining, le catch plantait lui-même.
        if (error.response?.status === 401) {
            dispatch(fetchingUserFailure("You don't have the rights to perform this action."));
        } else if (!error.response) {
            dispatch(fetchingUserFailure("Session ou connexion indisponible. Réessayez."));
        } else {
            dispatch(fetchingUserFailure("Can't find the user (mail) in database."));
        }
        console.error("User info request failed:", error);
    }
};

export const getUserInfoDiff = async (filters, setIsActiveDiffSearchError) => {
    try {
        setIsActiveDiffSearchError(null);
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/search?${filters}`, {
            headers: { 'Authorization' : token }
        })
        if (res.status === 200) {
            return res.data;
        }
    } catch (error) {
        if (error.response?.status === 401) {
            setIsActiveDiffSearchError("You don't have the rights to perform this action.");
        } else if (!error.response) {
            setIsActiveDiffSearchError("Session ou connexion indisponible. Réessayez.");
        } else {
            setIsActiveDiffSearchError("Can't find the user (mail) in database.");
        }
        console.error("Diff search request failed:", error);
    }
};

// ----------------------------------------------------------------

export const getUserDevices = async (dispatch, upn, signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/devices/${upn}`, {
            headers: { 'Authorization' : token },
            signal: signal
        });
        if (res.status === 200) {
            dispatch(fetchingDeviceSuccess(res.data));
            return res;
        }
    } catch {
        console.error("Devices not found for this user in database");
    }
};

// ----------------------------------------------------------------

export const getUserGroups = async (dispatch, upn, signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/groups/${upn}`, {
            headers: { 'Authorization' : token },
            signal: signal
        });
        if (res.status === 200) {
            dispatch(fetchingGroupSuccess(res.data));
            return res;
        }
    } catch {
        console.error("Groups not found for this user in database");
    }
};

// ----------------------------------------------------------------

export const getUserApplications = async (dispatch, upn, signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/applications/${upn}`, {
            headers: { 'Authorization' : token },
            signal: signal
        })
        if (res.status === 200) {
            dispatch(fetchingApplicationSuccess(res.data));
            return res;
        }
    } catch {
        console.error("Applications not found for this user in database");
    }
};

// ----------------------------------------------------------------

export const getUserDelegations = async (dispatch, upn, signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/delegations/${upn}`, {
            headers: { 'Authorization' : token },
            signal: signal
        })
        if (res.status === 200) {
            dispatch(fetchingDelegationSuccess(res.data));
            return res;
        }
    } catch {
        console.error("Delegations not found for this user in database");
    }
};

// ----------------------------------------------------------------

export const getUserOrganization = async (dispatch, upn, signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/organization/${upn}`, {
            headers: { 'Authorization' : token },
            signal: signal
        })
        if (res.status === 200) {
            dispatch(fetchingOrganizationSuccess(res.data));
            return res;
        }
    } catch {
        console.error("Organization not found for this user in database");
    }
};

// ----------------------------------------------------------------

export const getCurrentSearch = async (dispatch, filterRow, signal, page = 1, limit = 20, sorting = { sortField: 'displayName', sortOrder: 'asc' }, isInitialLoad = true) => {
    try {
        if (isInitialLoad) {
            dispatch(fetchingAdvancedSearchStart());
        } else {
            dispatch(fetchingPageStart());
        }
        
        const token = await getToken();

        // 1. On récupère le type d'info (Users, Devices...) depuis la première ligne
        const filtersInformation = filterRow[0]?.information || 'Users';

        // 2. On sérialise le tableau complet des filtres
        const advancedFilters = encodeURIComponent(JSON.stringify(filterRow));

        // 3. Construction de l'URL
        const queryParams = [
            `filtersInformation=${filtersInformation}`,
            `advancedFilters=${advancedFilters}`,
            `page=${page}`,
            `limit=${limit}`,
            `sortField=${sorting.sortField}`,
            `sortOrder=${sorting.sortOrder}`
        ].join('&');

        const res = await axios.get(`${baseUrl}/api/search?${queryParams}`, {
                headers: { 'Authorization': token },
                signal: signal,
            }
        );

        if (res.status === 200) {
            dispatch(fetchingAdvancedSearchSuccess(res.data));
            return res.data;
        }
    } catch (error) {
        if (axios.isCancel(error)) {
            // Requête annulée, on ne fait rien
            return;
        }
        if (error.response?.status === 401) {
            dispatch(fetchingAdvancedSearchFailure("You don't have the rights to perform this action."));
        } else {
            dispatch(fetchingAdvancedSearchFailure("Current search returned nothing"));
        }
        console.error("Current search returned nothing", error);
    }
};

export const getCurrentSearchNested = async (filterRow, signal, page = 1, limit = 20, sorting = { sortField: 'displayName', sortOrder: 'asc' }) => {
    try {
        const token = await getToken();

        const filtersInformation = filterRow[0]?.information || 'Users';

        const advancedFilters = encodeURIComponent(JSON.stringify(filterRow));

        const queryParams = [
            `filtersInformation=${filtersInformation}`,
            `advancedFilters=${advancedFilters}`,
            `page=${page}`,
            `limit=${limit}`,
            `sortField=${sorting.sortField}`,
            `sortOrder=${sorting.sortOrder}`
        ].join('&');

        const res = await axios.get(`${baseUrl}/api/search?${queryParams}`, {
                headers: { Authorization: token },
                signal
            }
        );

        if (res.status === 200) {
            return res.data?.data || [];
        }

    } catch (error) {
        if (!axios.isCancel(error)) {
            console.error("Error fetching nested search:", error);
        }
        throw error;
    }
};

export const getCurrentSearchOption = async (model, property, signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/searchOption?&model=${model}&property=${property}`, {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch (error) {
        console.error("Current search option returned nothing");
    }
};

export const getCurrentSearchUsers = async (input) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/searchUsers?&input=${input}`, {
            headers: { 'Authorization' : token }
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch (error) {
        console.error("Current search option returned nothing");
    }
};

export const getAllEtoile = async () => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/searchDepartment`, {
            headers: { 'Authorization' : token }
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch (error) {
        console.error("Current search option returned nothing");
    }
};

export const exportSearchCSV = async (filterRow) => {
    const token = await getToken();

    const filtersInformation = filterRow[0]?.information || 'Users';
    const advancedFilters = encodeURIComponent(JSON.stringify(filterRow));

    const url = `${baseUrl}/api/search/export?filtersInformation=${filtersInformation}&advancedFilters=${advancedFilters}`;

    const res = await axios.get(url, {
        headers: { Authorization: token },
        responseType: 'blob',
    });

    const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = blobUrl;

    const contentDisposition = res.headers['content-disposition'];
    let filename = `export_${new Date().toISOString().slice(0, 10)}.csv`;

    if (contentDisposition) {
        const match = contentDisposition.match(/filename="([^"]+)"/);
        if (match?.[1]) filename = match[1];
    }

    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
};

// ----------------------------------------------------------------

export const getUsersWithoutManager = async (signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/dashboard/usersWithoutManager`, {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : users without manager error");
    }
};

export const getUsersMissingCustomAttributes = async (signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/dashboard/usersMissingCustomAttributes`, {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : users missing custom attributes error");
    }
};

export const getUsersWithDuplicateEmployeeId = async (signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/dashboard/usersDuplicateEmployeeId`, {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : users missing employee Id error");
    }
};

export const getUsersWithManagerDisabled = async (signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/dashboard/usersWithManagerDisabled`, {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : users with manager disabled error");
    }
};

export const getUsersNotInConstellationToutLeMonde = async (signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/dashboard/usersNotInConstellationToutLeMonde`, {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : users in Constellation tout le monde not found");
    }
};

export const getUsersNotInTeamToutLeMonde = async (signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/dashboard/usersNotInTeamToutLeMonde`, {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : users in Team tout le monde not found");
    }
};

export const getUsersBALWithManager = async (signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/dashboard/usersBALWithManager`, {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : users BAL with manager not found");
    }
};

export const getUsersBALWithoutSponsors = async (signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/dashboard/usersBALWithoutSponsors`, {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : users BAL without sponsors not found");
    }
};

export const getUsersBALOwnerOfGroup = async (signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/dashboard/usersBALOwnerOfGroup`, {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : users BAL owner of group not found");
    }
};

export const getUsersBALWithLicense = async (signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/dashboard/usersBALWithLicense`, {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : users BAL with license not found");
    }
}

export const getUsersWithoutLicenseIntune = async (signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/dashboard/usersWithoutLicenseIntune`, {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : users without license Intune not found");
    }
};

export const getUsersWithoutLicenseDefender = async (signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/dashboard/usersWithoutLicenseDefender`, {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : users without license Defender not found");
    }
};

export const getUsersManagerWithoutGrpSecManager = async (signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/dashboard/usersManagerWithoutGrpSecManager`, {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : users manager without group security manager not found");
    }
};

export const getUsersManagerWithoutCustomAttribute5 = async (signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/dashboard/usersManagerWithoutCustomAttribute5`, {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : users manager without custom attribute 5 not found");
    }
};

export const getUsersWithMultipleLaptop = async (signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/dashboard/usersWithMultipleLaptop`, {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : users with multiple laptop not found");
    }
};

export const getUsersWithoutLaptop = async (signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/dashboard/usersWithoutLaptop`, {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : users without laptop not found");
    }
};

export const getUsersExternalWithoutDesactivationDate = async (signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/dashboard/usersExternalWithoutDesactivationDate`, {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : external users without desactivation date not found");
    }
};

export const getUsersBALWithGroup = async (signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/dashboard/usersBALWithGroup`, {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : users BAL with group not found");
    }
};

// ----------------------------------------------------------------

export const postRemediationUsersWithoutManager = async (signal, userInformations) => {
    try {
        const token = await getToken();
        const res = await axios.post(`${baseUrl}/api/remediation/usersWithoutManager`,
        { userInformations: userInformations },
        {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : remediation users without manager doesn't work");
    }
};

export const postRemediationUsersWithoutCustomAttribute = async (signal, userInformations) => {
    try {
        const token = await getToken();
        const res = await axios.post(`${baseUrl}/api/remediation/usersWithoutCustomAttribute`,
        { userInformations: userInformations },
        {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : remediation users without custom attribute doesn't work");
    }
};

export const postRemediationUsersWithDuplicateEmployeeId = async (signal, userInformations) => {
    try {
        const token = await getToken();
        const res = await axios.post(`${baseUrl}/api/remediation/usersWithDuplicateEmployeeId`,
        { userInformations: userInformations },
        {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : remediation users with duplicate employee ID doesn't work");
    }
};

export const postRemediationUsersWithManagerDisabled = async (signal, userInformations) => {
    try {
        const token = await getToken();
        const res = await axios.post(`${baseUrl}/api/remediation/usersWithManagerDisabled`,
        { userInformations: userInformations },
        {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : remediation users with manager disabled doesn't work");
    }
};

export const postRemediationUsersNotInConstellationToutLeMonde = async (signal, userInformations) => {
    try {
        const token = await getToken();
        const res = await axios.post(`${baseUrl}/api/remediation/usersNotInConstellationToutLeMonde`,
        { userInformations: userInformations },
        {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : remediation users not in constellation tout le monde doesn't work");
    }
};

export const postRemediationUsersNotInTeamToutLeMonde = async (signal, userInformations) => {
    try {
        const token = await getToken();
        const res = await axios.post(`${baseUrl}/api/remediation/usersNotInTeamToutLeMonde`,
        { userInformations: userInformations },
        {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : remediation users not in team tout le monde doesn't work");
    }
};

export const postRemediationUsersWithoutLicenseIntune = async (signal, userInformations) => {
    try {
        const token = await getToken();
        const res = await axios.post(`${baseUrl}/api/remediation/usersWithoutLicenseIntune`,
        { userInformations: userInformations },
        {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : remediation users without license Intune doesn't work");
    }
};

export const postRemediationUsersWithoutLicenseDefender = async (signal, userInformations) => {
    try {
        const token = await getToken();
        const res = await axios.post(`${baseUrl}/api/remediation/usersWithoutLicenseDefender`,
        { userInformations: userInformations },
        {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : remediation users without license Defender doesn't work");
    }
};

export const postRemediationUsersBALWithManager = async (signal, userInformations) => {
    try {
        const token = await getToken();
        const res = await axios.post(`${baseUrl}/api/remediation/usersBALWithManager`,
        { userInformations: userInformations },
        {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : remediation BAL with manager doesn't work");
    }
};

export const postRemediationUsersBALWithoutSponsors = async (signal, userInformations) => {
    try {
        const token = await getToken();
        const res = await axios.post(`${baseUrl}/api/remediation/usersBALWithoutSponsors`,
        { userInformations: userInformations },
        {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : remediation BAL without sponsors doesn't work");
    }
};

export const postRemediationUsersBALOwnerOfGroup = async (signal, userInformations) => {
    try {
        const token = await getToken();
        const res = await axios.post(`${baseUrl}/api/remediation/usersBALOwnerOfGroup`,
        { userInformations: userInformations },
        {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : remediation BAL without sponsors doesn't work");
    }
};

export const postRemediationUsersBALWithLicense = async (signal, userInformations) => {
    try {
        const token = await getToken();
        const res = await axios.post(`${baseUrl}/api/remediation/usersBALWithLicense`,
        { userInformations: userInformations },
        {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : remediation BAL with license doesn't work");
    }
};

export const postRemediationUsersBALWithGroup = async (signal, userInformations) => {
    try {
        const token = await getToken();
        const res = await axios.post(`${baseUrl}/api/remediation/usersBALWithGroup`,
        { userInformations: userInformations },
        {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : remediation BAL with group doesn't work");
    }
};

export const postRemediationUsersManagerWithoutGrpSecManager = async (signal, userInformations) => {
    try {
        const token = await getToken();
        const res = await axios.post(`${baseUrl}/api/remediation/usersManagerWithoutGrpSecManager`,
        { userInformations: userInformations },
        {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : remediation manager without GRP_SEC_MANAGER doesn't work");
    }
};

export const postRemediationUsersManagerWithoutCustomAttribute5 = async (signal, userInformations) => {
    try {
        const token = await getToken();
        const res = await axios.post(`${baseUrl}/api/remediation/usersManagerWithoutCustomAttribute5`,
        { userInformations: userInformations },
        {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : remediation manager without CustomAttribute5 doesn't work");
    }
};

export const postRemediationUsersExternalWithoutDesactivationDate = async (signal, userInformations) => {
    try {
        const token = await getToken();
        const res = await axios.post(`${baseUrl}/api/remediation/usersExternalWithoutDesactivationDate`,
        { userInformations: userInformations },
        {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch {
        console.error("Request : remediation users external without desactivation date doesn't work");
    }
};

// ----------------------------------------------------------------

export const postAdministrationAddRSSI = async (signal, objectId, etoile) => {
    try {
        const token = await getToken();
        const res = await axios.post(`${baseUrl}/api/administration/addRSSI`,
        { objectId: objectId, etoile: etoile },
        {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch (error) {
        console.error("Request : add RSSI administration function doesn't work");
        if (error.response) {
            throw error.response.data;
        }
        throw { message: "Erreur de connexion au serveur" };
    }
};

export const getAdministrationRSSIList = async () => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/administration/rssi`, {
            headers: { 'Authorization': token },
        });
        if (res.status === 200) return res.data;
    } catch (error) {
        console.error("Request : get RSSI list doesn't work");
        throw { message: "Erreur de connexion au serveur" };
    }
};

export const postAdministrationRemoveRSSI = async (signal, objectId, etoile) => {
    try {
        const token = await getToken();
        const res = await axios.post(`${baseUrl}/api/administration/removeRSSI`,
        { objectId: objectId, etoile: etoile },
        {
            headers: { 'Authorization' : token },
            signal: signal,
        });
        if (res.status === 200) {
            return res.data;
        }
    } catch (error) {
        console.error("Request : remove RSSI administration function doesn't work");
        if (error.response) {
            throw error.response.data;
        }
        throw { message: "Erreur de connexion au serveur" };
    }
};

// ----------------------------------------------------------------

export const getAiContext = async () => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/ai/context`, {
            headers: { 'Authorization': token },
        });
        if (res.status === 200) return res.data;
    } catch (error) {
        console.error("Request : get AI context doesn't work");
        return null;
    }
};

export const exportAiQueryCSV = async ({ collection, operation, query, outputCollection }) => {
    const token = await getToken();
    const res = await axios.post(`${baseUrl}/api/ai/export`,
        { collection, operation, query, outputCollection },
        { headers: { 'Authorization': token }, responseType: 'blob' }
    );
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    const disposition = res.headers['content-disposition'];
    const match = disposition?.match(/filename="([^"]+)"/);
    link.setAttribute('download', match?.[1] || 'export_ai.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

export const postAiAnalyze = async ({ description, collection, count, hasMore, data }) => {
    try {
        const token = await getToken();
        const res = await axios.post(`${baseUrl}/api/ai/analyze`,
            { description, collection, count, hasMore, data },
            { headers: { 'Authorization': token } }
        );
        if (res.status === 200) return res.data;
    } catch (error) {
        console.error('AI analyze error:', error);
        return null;
    }
};

export const postAiQuery = async (question, signal) => {
    try {
        const token = await getToken();
        const res = await axios.post(`${baseUrl}/api/ai/query`,
            { question },
            { headers: { 'Authorization': token }, signal }
        );
        if (res.status === 200) return res.data;
    } catch (error) {
        if (axios.isCancel(error)) return null;
        console.error('AI query error:', error);
        return error.response?.data || { error: 'Erreur serveur' };
    }
};

// ----------------------------------------------------------------

export const postStartMigrationInterEtoile = async (usersInformations, socketId, targetDepartment, targetDomain) => {
    try {
        const token = await getToken();

        // On utilise POST car on envoie des données
        const res = await axios.post(`${baseUrl}/api/migration/usersInterEtoile`,
            {
                usersInformations: usersInformations,
                socketId: socketId,
                targetDepartment: targetDepartment,
                targetDomain: targetDomain,
            },
            {
                headers: { 'Authorization' : token },
            }
        );

        if (res.status === 202 || res.status === 200) {
            console.log("Ordre de migration envoyé au serveur");
            return res.data;
        }

    } catch (error) {
        console.error("Erreur lors de l'appel testSocket :", error);
        throw error;
    }
};
// ----------------------------------------------------------------
// Defender — Sécurité
// Toutes ces lectures sont servies depuis MongoDB côté serveur : l'API
// Advanced Hunting est plafonnée à 15 appels/minute pour tout le tenant.
// ----------------------------------------------------------------

const buildQuery = (params) => {
    const search = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') search.append(key, value);
    });
    const query = search.toString();
    return query ? `?${query}` : '';
};

export const getSecuritySummary = async (signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/defender/summary`, {
            headers: { 'Authorization': token },
            signal,
        });
        return res.data;
    } catch (error) {
        if (axios.isCancel(error)) return null;
        console.error('Defender summary request failed:', error);
        throw error;
    }
};

export const getSecurityCves = async (params, signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/defender/cves${buildQuery(params)}`, {
            headers: { 'Authorization': token },
            signal,
        });
        return res.data;
    } catch (error) {
        if (axios.isCancel(error)) return null;
        console.error('Defender CVE list request failed:', error);
        throw error;
    }
};

export const getSecurityCveDetail = async (cveCode, signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/defender/cves/${encodeURIComponent(cveCode)}`, {
            headers: { 'Authorization': token },
            signal,
        });
        return res.data;
    } catch (error) {
        if (axios.isCancel(error)) return null;
        console.error('Defender CVE detail request failed:', error);
        throw error;
    }
};

export const getSecurityDevices = async (params, signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/defender/devices${buildQuery(params)}`, {
            headers: { 'Authorization': token },
            signal,
        });
        return res.data;
    } catch (error) {
        if (axios.isCancel(error)) return null;
        console.error('Defender device list request failed:', error);
        throw error;
    }
};

export const getSecurityDeviceVulnerabilities = async (deviceId, signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(
            `${baseUrl}/api/defender/devices/${encodeURIComponent(deviceId)}/vulnerabilities`,
            { headers: { 'Authorization': token }, signal }
        );
        return res.data;
    } catch (error) {
        if (axios.isCancel(error)) return null;
        console.error('Defender device vulnerabilities request failed:', error);
        throw error;
    }
};

// Catalogue des applications publiées dans Intune, avec leurs compteurs de
// déploiement. Servi depuis MongoDB.
export const getIntuneApps = async (params, signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/intune-apps${buildQuery(params)}`, {
            headers: { 'Authorization': token }, signal,
        });
        return res.data;
    } catch (error) {
        if (axios.isCancel(error)) return null;
        console.error('Intune apps request failed:', error);
        throw error;
    }
};

// Fiche d'une application : affectations et état poste par poste.
// `versionBelow` filtre les postes portant une version antérieure — la
// comparaison n'étant pas lexicographique, elle se fait côté serveur.
export const getIntuneApp = async (appId, params, signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(
            `${baseUrl}/api/intune-apps/${encodeURIComponent(appId)}${buildQuery(params)}`,
            { headers: { 'Authorization': token }, signal }
        );
        return res.data;
    } catch (error) {
        if (axios.isCancel(error)) return null;
        console.error('Intune app detail request failed:', error);
        throw error;
    }
};

// Catalogue des applications constatées sur le parc, tous postes confondus.
export const getDetectedApps = async (params, signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/intune-apps/detected${buildQuery(params)}`, {
            headers: { 'Authorization': token }, signal,
        });
        return res.data;
    } catch (error) {
        if (axios.isCancel(error)) return null;
        console.error('Detected apps request failed:', error);
        throw error;
    }
};

export const getDetectedAppDevices = async (detectedAppId, signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(
            `${baseUrl}/api/intune-apps/detected/${encodeURIComponent(detectedAppId)}/devices`,
            { headers: { 'Authorization': token }, signal }
        );
        return res.data;
    } catch (error) {
        if (axios.isCancel(error)) return null;
        console.error('Detected app devices request failed:', error);
        throw error;
    }
};

// Applications d'un appareil : ce qu'Intune y déploie et ce qui y est constaté.
// Servi depuis MongoDB, alimenté par le job horaire (déploiement) et le passage
// de 22 h (inventaire constaté). Chargé à la demande : un poste porte jusqu'à
// 533 applications détectées, elles n'ont pas leur place dans la réponse
// d'ouverture de la fiche.
export const getDeviceApplications = async (deviceIdIntune, signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(
            `${baseUrl}/api/intune-apps/device/${encodeURIComponent(deviceIdIntune)}`,
            { headers: { 'Authorization': token }, signal }
        );
        return res.data;
    } catch (error) {
        if (axios.isCancel(error)) return null;
        console.error('Device applications request failed:', error);
        throw error;
    }
};

// Détail de conformité Intune d'un appareil, interrogé EN DIRECT auprès de Graph
// côté serveur (cache 2 min).
//
// N'est PLUS appelé à l'ouverture de la fiche appareil : celle-ci sert le détail
// depuis intunecompliancesettings, alimentée par les synchros de 00 h et 12 h.
// Cet appel est désormais derrière le bouton « Rafraîchir » de la fiche — un
// appel par stratégie sur l'endpoint le plus étranglé de Graph, ce qui ne se
// justifie que sur demande explicite : vérifier une remédiation qu'on vient
// d'appliquer, par exemple.
export const getDeviceComplianceDetail = async (deviceId, signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(
            `${baseUrl}/api/devices/${encodeURIComponent(deviceId)}/compliance-detail`,
            { headers: { 'Authorization': token }, signal }
        );
        return res.data;
    } catch (error) {
        if (axios.isCancel(error)) return null;
        if (error.response?.status === 404) return null;
        console.error('Device compliance detail request failed:', error);
        throw error;
    }
};

// Vue 360° d'un appareil — assemblage Entra + Intune + Defender + CVE,
// servi depuis MongoDB (aucun appel Graph, la page doit s'ouvrir instantanément).
export const searchOverviewDevices = async (query, signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/device-overview/search?q=${encodeURIComponent(query)}`, {
            headers: { 'Authorization': token }, signal,
        });
        return res.data;
    } catch (error) {
        if (axios.isCancel(error)) return null;
        console.error('Device search request failed:', error);
        throw error;
    }
};

export const getDeviceOverview = async (key, signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/device-overview/${encodeURIComponent(key)}`, {
            headers: { 'Authorization': token }, signal,
        });
        return res.data;
    } catch (error) {
        if (axios.isCancel(error)) return null;
        console.error('Device overview request failed:', error);
        throw error;
    }
};

// ─── Tâches planifiées (administration) ───

export const getJobs = async (signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(`${baseUrl}/api/jobs`, {
            headers: { 'Authorization': token }, signal,
        });
        return res.data;
    } catch (error) {
        if (axios.isCancel(error)) return null;
        console.error('Jobs request failed:', error);
        throw error;
    }
};

export const getJobsJournal = async (limit = 60, jobId = null, signal) => {
    try {
        const token = await getToken();
        const params = new URLSearchParams({ limit: String(limit) });
        if (jobId) params.set('jobId', jobId);
        const res = await axios.get(`${baseUrl}/api/jobs/journal?${params}`, {
            headers: { 'Authorization': token }, signal,
        });
        return res.data;
    } catch (error) {
        if (axios.isCancel(error)) return null;
        console.error('Jobs journal request failed:', error);
        throw error;
    }
};

export const postJobRun = async (jobId) => {
    try {
        const token = await getToken();
        const res = await axios.post(`${baseUrl}/api/jobs/${encodeURIComponent(jobId)}/run`, {}, {
            headers: { 'Authorization': token },
        });
        return res.data;
    } catch (error) {
        console.error('Job run request failed:', error);
        throw error.response?.data || { error: 'Erreur de connexion au serveur' };
    }
};

export const postJobSchedule = async (jobId, active) => {
    try {
        const token = await getToken();
        const res = await axios.post(`${baseUrl}/api/jobs/${encodeURIComponent(jobId)}/schedule`, { active }, {
            headers: { 'Authorization': token },
        });
        return res.data;
    } catch (error) {
        console.error('Job schedule request failed:', error);
        throw error.response?.data || { error: 'Erreur de connexion au serveur' };
    }
};

// ─── Historique de conformité (vue d'ensemble Sécurité) ───

export const getConformiteHistorique = async (filtres = {}, signal) => {
    try {
        const token = await getToken();
        const params = new URLSearchParams();
        Object.entries(filtres).forEach(([cle, valeur]) => {
            if (valeur !== null && valeur !== undefined && valeur !== '') params.set(cle, String(valeur));
        });
        const res = await axios.get(`${baseUrl}/api/defender/conformite-historique?${params}`, {
            headers: { 'Authorization': token }, signal,
        });
        return res.data;
    } catch (error) {
        if (axios.isCancel(error)) return null;
        console.error('Compliance history request failed:', error);
        throw error;
    }
};

/* ─── Conformité Intune, par stratégie ─── */

export const getCompliancePolicies = async (params = {}, signal) => {
    try {
        const token = await getToken();
        const requete = new URLSearchParams();
        Object.entries(params).forEach(([clef, valeur]) => {
            if (valeur !== '' && valeur !== null && valeur !== undefined && valeur !== false) {
                requete.append(clef, valeur);
            }
        });
        const res = await axios.get(`${baseUrl}/api/intune/compliance-policies?${requete.toString()}`, {
            headers: { 'Authorization': token }, signal,
        });
        return res.data;
    } catch (error) {
        if (axios.isCancel(error)) return null;
        console.error('Compliance policies request failed:', error);
        throw error;
    }
};

export const getCompliancePolicy = async (policyId, signal) => {
    try {
        const token = await getToken();
        const res = await axios.get(
            `${baseUrl}/api/intune/compliance-policies/${encodeURIComponent(policyId)}`,
            { headers: { 'Authorization': token }, signal }
        );
        return res.data;
    } catch (error) {
        if (axios.isCancel(error)) return null;
        if (error.response?.status === 404) return null;
        console.error('Compliance policy detail request failed:', error);
        throw error;
    }
};
